// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Skipper tracker, checks, downloads (Phase 3), § Portal routes
// ==============================================
// The Skipper-Tracker artboard as plain rules: the crew × papers grid's
// columns and cells, the stat tiles, the "Needs your look" lines and the
// "Coming up" lines. Everything comes from the API's RequestDetail (states,
// dates, never a paper's contents); dates are the request's local dates.
// ==============================================

import type { ComingUp, RequestDetail, SkipperRecipientView } from '../types';
import { accessEndDate, firstName, fmtDay, fmtDayYear, localDateOf } from '../dates';

export type Item = SkipperRecipientView['items'][number];
export type CellTone = 'in' | 'wait' | 'look' | 'na' | 'off' | 'ended';
export interface Cell { tone: CellTone; title: string; sub: string | null }

/** The grid's paper columns: every type asked of anyone, in the order first asked. */
export function paperColumns(d: Pick<RequestDetail, 'recipients'>): Array<{ type: string; label: string }> {
  const out: Array<{ type: string; label: string }> = [];
  for (const r of d.recipients) for (const i of r.items) if (!out.some((c) => c.type === i.type)) out.push({ type: i.type, label: i.label });
  return out;
}

export const crewName = (r: Pick<SkipperRecipientView, 'name' | 'email'>) => r.name?.trim() || r.email;

/** When someone was last reminded, as the grid says it: "Reminded today", "Reminded Mon 28 Sep". */
export function remindedText(r: Pick<SkipperRecipientView, 'lastRemindedAt' | 'claimed' | 'linkSent'>, tz: string, today: string): string {
  if (!r.linkSent) return 'Not sent yet';
  if (!r.lastRemindedAt) return r.claimed ? 'Not reminded yet' : 'Hasn’t opened the link';
  const on = localDateOf(r.lastRemindedAt, tz);
  return on === today ? 'Reminded today' : `Reminded ${fmtDay(on)}`;
}

/** One cell of the grid. */
export function cellFor(i: Item | undefined, r: SkipperRecipientView, tz: string, today: string): Cell {
  if (!i) return { tone: 'off', title: 'Not requested', sub: null };
  const until = i.grant?.active ? `Access until ${fmtDay(accessEndDate(i.grant.expiresAt, tz))}` : null;
  if (i.state === 'not_needed') return { tone: 'na', title: 'Not needed', sub: i.reason };
  if (i.state === 'needs_attention') {
    const title = i.check?.kind === 'passport_validity' ? 'Expires too soon' : i.check?.kind === 'before_arrival' ? 'Ends before arrival' : 'Needs your look';
    return { tone: 'look', title, sub: i.question ? 'You asked about it' : i.check ? fmtDayYear(i.check.expiresOn) : until };
  }
  if (i.state === 'accepted') return { tone: 'in', title: 'Accepted', sub: until };
  if (i.grant?.active) return { tone: 'in', title: 'In', sub: until };
  if (i.state === 'provided' && i.grant) return { tone: 'ended', title: i.grant.revokedAt ? `${firstName(crewName(r))} withdrew it` : 'Access ended', sub: null };
  return { tone: 'wait', title: 'Waiting', sub: remindedText(r, tz, today) };
}

/** "3 of 4 · 1 to check" under a name: required papers in, of the required total. */
export function rowSummary(r: SkipperRecipientView): string {
  const req = r.items.filter((i) => i.required);
  const inn = req.filter((i) => i.state === 'provided' || i.state === 'not_needed' || i.state === 'accepted').length;
  const look = r.items.filter((i) => i.state === 'needs_attention').length;
  return `${inn} of ${req.length}${look ? ` · ${look} to check` : ''}`;
}

/** The fourth tile: "Due Sat 10 Oct · 12 days", "Due today", "Due 3 days ago". */
export function dueTile(d: Pick<RequestDetail, 'dueOn' | 'stats'>): { label: string; value: string; unit: string } | null {
  if (!d.dueOn) return null;
  const n = d.stats.dueInDays ?? 0;
  if (n === 0) return { label: `Due ${fmtDay(d.dueOn)}`, value: 'Today', unit: '' };
  if (n < 0) return { label: `Was due ${fmtDay(d.dueOn)}`, value: String(-n), unit: -n === 1 ? 'day ago' : 'days ago' };
  return { label: `Due ${fmtDay(d.dueOn)}`, value: String(n), unit: n === 1 ? 'day' : 'days' };
}

export interface LookLine { itemId: string; recipientId: string; crew: string; title: string; body: string; question: string | null; draft: string }

/** The "Needs your look" panel: each paper flagged, why, and a question to start from. */
export function lookLines(d: Pick<RequestDetail, 'recipients' | 'arriveOn'>): LookLine[] {
  const out: LookLine[] = [];
  for (const r of d.recipients) for (const i of r.items) {
    if (i.state !== 'needs_attention') continue;
    const who = firstName(crewName(r), 'They');
    const label = i.label.toLowerCase();
    if (i.check?.kind === 'passport_validity') {
      out.push({
        itemId: i.id, recipientId: r.id, crew: who, question: i.question,
        title: `${who}’s passport expires ${fmtDayYear(i.check.expiresOn)}`,
        body: `You asked for a passport valid until at least ${fmtDayYear(i.check.neededUntil)}.`,
        draft: `Your passport expires ${fmtDayYear(i.check.expiresOn)}, but it needs to be valid until at least ${fmtDayYear(i.check.neededUntil)}. Can you renew it before we sail, or tell me your plan?`,
      });
    } else if (i.check?.kind === 'before_arrival') {
      out.push({
        itemId: i.id, recipientId: r.id, crew: who, question: i.question,
        title: `${who}’s ${label} ends ${fmtDayYear(i.check.expiresOn)}`,
        body: `That is before we arrive on ${fmtDay(i.check.neededUntil)}.`,
        draft: `Your ${label} ends ${fmtDayYear(i.check.expiresOn)}, before we arrive on ${fmtDay(i.check.neededUntil)}. Can you send one that covers the whole passage?`,
      });
    } else {
      out.push({ itemId: i.id, recipientId: r.id, crew: who, question: i.question, title: `${who}’s ${label} needs your look`, body: 'Open it to check it.', draft: `A question about your ${label}: ` });
    }
  }
  return out;
}

/** One "Coming up" line: its date, its text, and whether it is the due date (amber). */
export function comingUpText(c: ComingUp): { on: string; text: string; due: boolean } {
  switch (c.kind) {
    case 'reminders': return { on: c.on, due: false, text: `Reminders to ${c.people.map((p) => `${firstName(p.name, p.name)} (${p.outstanding})`).join(', ')}` };
    case 'status': return { on: c.on, due: false, text: 'Your status email' };
    case 'due': return { on: c.on, due: true, text: 'Due' };
    case 'access_notice': return { on: c.on, due: false, text: `Heads-up: ${firstName(c.name, c.name)}’s access ends ${fmtDay(c.lastDay)}` };
    case 'access_ends': return { on: c.on, due: false, text: `${firstName(c.name, c.name)}’s access ends` };
  }
}

/** Who "Remind the N now" would email: not done, and not reminded too recently. */
export const remindable = (d: Pick<RequestDetail, 'status' | 'recipients'>) =>
  d.status === 'open' ? d.recipients.filter((r) => r.linkSent && !r.done && r.outstanding > 0 && !r.remindAfter) : [];

/** People whose papers the skipper can see now — the "Ask to extend" button. */
export const hasActiveGrant = (r: SkipperRecipientView) => r.items.some((i) => i.grant?.active);

export const STATUS_WORD: Record<RequestDetail['status'], { word: string; dot: string; text: string }> = {
  draft: { word: 'DRAFT', dot: 'bg-slate-400', text: 'text-slate-300' },
  open: { word: 'COLLECTING', dot: 'bg-emerald-400', text: 'text-emerald-300' },
  complete: { word: 'ALL IN', dot: 'bg-emerald-400', text: 'text-emerald-300' },
  closed: { word: 'CLOSED', dot: 'bg-slate-500', text: 'text-slate-400' },
  cancelled: { word: 'CANCELLED', dot: 'bg-slate-500', text: 'text-slate-400' },
};
